import React from "react";
import Navbar from "../../component/Navbar/Navbar";
import Footer from "../../component/Footer/Footer";
import TrustedBy from "../../component/Home/TrustedBy";
import { Link } from "react-router-dom";
import {
  ArrowLongRightIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  CpuChipIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  BoltIcon,
  GlobeAltIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ServerStackIcon,
  CalculatorIcon,
} from "@heroicons/react/24/outline";

const platforms = [
  { name: "POS Terminals", icon: CpuChipIcon, description: "Run on any POS device" },
  { name: "Android", icon: DevicePhoneMobileIcon, description: "Native Android app" },
  { name: "iOS", icon: DevicePhoneMobileIcon, description: "Native iOS app" },
  { name: "Web", icon: ComputerDesktopIcon, description: "Full web platform" },
];

const features = [
  {
    icon: BoltIcon,
    title: "Real-time Processing",
    description: "Instant transaction processing with sub-second latency, ensuring smooth operations even during peak loads.",
  },
  {
    icon: ShieldCheckIcon,
    title: "Enterprise Security",
    description: "Bank-grade encryption, multi-factor authentication, and compliance with international regulatory standards.",
  },
  {
    icon: ChartBarIcon,
    title: "Advanced Analytics",
    description: "Comprehensive dashboards with real-time insights, revenue tracking, and predictive analytics.",
  },
  {
    icon: GlobeAltIcon,
    title: "Multi-Region Support",
    description: "Deploy across multiple regions with localized configurations, currencies, and regulatory compliance.",
  },
  {
    icon: UserGroupIcon,
    title: "Agent Management",
    description: "Complete agent network management with hierarchical structures, commissions, and performance tracking.",
  },
  {
    icon: ServerStackIcon,
    title: "99.9% Uptime",
    description: "Cloud-native architecture with auto-scaling, load balancing, and disaster recovery built in.",
  },
];

const highlights = [
  { label: "Operators", value: "Growing", sublabel: "White-label network" },
  { label: "Platform", value: "Multi-device", sublabel: "POS, Android, iOS, Web" },
  { label: "Architecture", value: "Cloud-native", sublabel: "Auto-scaling SaaS" },
  { label: "Coverage", value: "Nationwide", sublabel: "Agent network" },
];

const PoolPage = () => {
  return (
    <main>
      <Navbar variant="dark" />

      {/* Hero */}
      <section id="devdesk-pool" className="relative min-h-screen flex items-center overflow-hidden bg-gray-950">
        {/* Background grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff06_1px,transparent_1px),linear-gradient(to_bottom,#ffffff06_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#0186ef]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/3 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-16 w-full pt-32 pb-20 lg:pt-40 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full mb-8 border border-white/10">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400"></span>
                </span>
                <span className="text-sm font-medium text-gray-300">
                  Live &middot; powering betting operators nationwide
                </span>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.1] tracking-tight">
                Devdesk
                <span className="block text-[#0186ef]">Pool</span>
              </h1>

              <p className="mt-8 text-lg lg:text-xl text-gray-400 leading-relaxed max-w-lg">
                The next-generation SaaS platform revolutionizing pool betting. Built to run everywhere: POS terminals, Android, iOS, and web.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold text-white bg-[#0186ef] rounded-xl hover:bg-[#0070d0] transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/25 hover:-translate-y-0.5 group"
                >
                  Request a Demo
                  <ArrowLongRightIcon className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Link>
                <a
                  href="https://www.debuxtonfixedodds.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold text-gray-300 border border-white/20 rounded-xl hover:bg-white/5 hover:border-white/30 transition-all duration-300"
                >
                  See it Live
                  <GlobeAltIcon className="ml-2 w-5 h-5" />
                </a>
              </div>

              {/* Platform badges */}
              <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {platforms.map((platform) => (
                  <div key={platform.name} className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10">
                    <platform.icon className="w-5 h-5 text-[#0186ef]" />
                    <div>
                      <div className="text-sm font-medium text-white">{platform.name}</div>
                      <div className="text-xs text-gray-500">{platform.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Product mockup */}
            <div className="relative">
              <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-6 shadow-2xl border border-white/10">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                  <span className="ml-2 text-xs text-gray-500">pool.devdesk.ng</span>
                </div>

                {/* Dashboard mockup */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between bg-white/5 rounded-xl p-4">
                    <div>
                      <div className="text-xs text-gray-500">Today's Revenue</div>
                      <div className="text-2xl font-bold text-white mt-1">₦12,450,000</div>
                    </div>
                    <div className="text-sm text-green-400 font-medium">+23.5% ↑</div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white/5 rounded-xl p-3 text-center">
                      <div className="text-xs text-gray-500">Bets</div>
                      <div className="text-lg font-bold text-white">4,521</div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-3 text-center">
                      <div className="text-xs text-gray-500">Agents</div>
                      <div className="text-lg font-bold text-white">382</div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-3 text-center">
                      <div className="text-xs text-gray-500">Wins</div>
                      <div className="text-lg font-bold text-white">1,247</div>
                    </div>
                  </div>

                  {/* Chart bars mockup */}
                  <div className="bg-white/5 rounded-xl p-4">
                    <div className="text-xs text-gray-500 mb-3">Weekly Performance</div>
                    <div className="flex items-end gap-2 h-24">
                      {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                        <div key={i} className="flex-1 rounded-t-md bg-gradient-to-t from-[#0186ef] to-blue-400" style={{ height: `${h}%` }} />
                      ))}
                    </div>
                    <div className="flex justify-between mt-2 text-[10px] text-gray-600">
                      <span>Sun</span><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 bg-[#0186ef]/10 rounded-xl p-3 border border-[#0186ef]/20">
                    <BoltIcon className="w-5 h-5 text-[#0186ef]" />
                    <div className="text-sm text-gray-300">
                      All systems operational &middot; <span className="text-green-400">100% uptime today</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating notification */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl px-4 py-3 border border-gray-100 animate-bounce">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <CurrencyDollarIcon className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-gray-900">New Win!</div>
                    <div className="text-[10px] text-gray-500">₦250,000 payout</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-16 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {highlights.map((item) => (
              <div key={item.label} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-gray-900">{item.value}</div>
                <div className="mt-2 text-sm font-semibold text-gray-900">{item.label}</div>
                <div className="text-xs text-gray-500">{item.sublabel}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 lg:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white rounded-full mb-6 border border-gray-200">
              <div className="w-1.5 h-1.5 rounded-full bg-[#0186ef]" />
              <span className="text-xs font-semibold text-[#0186ef] uppercase tracking-wider">Features</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Everything you need to run pool operations
            </h2>
            <p className="text-lg text-gray-500">
              Built from the ground up for the betting industry with enterprise-grade reliability and modern architecture.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
              >
                <div className="w-12 h-12 rounded-xl bg-[#0186ef]/10 flex items-center justify-center mb-6 group-hover:bg-[#0186ef] transition-colors duration-300">
                  <feature.icon className="w-6 h-6 text-[#0186ef] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-500 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Spotlight */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full mb-6 border border-blue-100">
                <div className="w-1.5 h-1.5 rounded-full bg-[#0186ef]" />
                <span className="text-xs font-semibold text-[#0186ef] uppercase tracking-wider">Our Clients</span>
              </div>

              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Powering real betting operations
              </h2>

              <p className="text-lg text-gray-500 leading-relaxed mb-6">
                Devdesk Pool is live and processing transactions for a growing network of
                licensed betting operators nationwide. From POS terminals across retail
                locations to web and mobile platforms, the system handles end-to-end
                pool betting operations for every brand built on it.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  "Seamless multi-platform experience across POS, web, and mobile",
                  "Real-time transaction processing with instant settlements",
                  "Comprehensive agent management and commission tracking",
                  "Advanced analytics and regulatory compliance",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="mt-1 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-600">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-[#0186ef] to-blue-700 rounded-3xl p-8 lg:p-12 text-white">
                <blockquote className="text-xl lg:text-2xl font-medium leading-relaxed mb-8">
                  "Devdesk Pool gives us multi-platform capability. Our agents
                  work from POS terminals, phones, or the web, all seamlessly integrated."
                </blockquote>
                <div className="flex items-center gap-3">
                  <img src="/img/clients/debuxton.png" alt="D.Buxton United Pools" className="w-10 h-10 rounded-full object-contain bg-white p-1" />
                  <div>
                    <div className="font-semibold text-sm">D.Buxton United Pools</div>
                    <div className="text-xs text-blue-200">Live since 2025</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Operator brand wall */}
          <TrustedBy heading="A growing network. One platform." />
        </div>
      </section>

      {/* Auditra cross-sell */}
      <section className="py-16 bg-gray-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 bg-white rounded-2xl p-8 border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <CalculatorIcon className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900">Also from DevDesk: Auditra</div>
                <div className="text-sm text-gray-500">AI-powered accounting for the modern business, coming soon.</div>
              </div>
            </div>
            <Link
              to="/products/auditra"
              className="inline-flex items-center px-6 py-3 text-sm font-semibold text-emerald-700 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-all duration-300 group flex-shrink-0"
            >
              Explore Auditra
              <ArrowLongRightIcon className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 lg:py-32 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#0186ef]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 lg:px-16 text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Ready to revolutionize your pool operations?
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-10">
            Join the growing network of operators already using Devdesk Pool to power their business.
            Get started today with a personalised demo.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-[#0186ef] rounded-xl hover:bg-[#0070d0] transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/25 hover:-translate-y-0.5 group"
            >
              Request a Demo
              <ArrowLongRightIcon className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="https://www.debuxtonfixedodds.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-gray-300 border border-white/20 rounded-xl hover:bg-white/5 transition-all duration-300"
            >
              See It Live
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default PoolPage;
